import { type Editor } from '@tiptap/core';
import { memo, useEffect, useRef } from 'react';

import { SkillExplanation, SkillTooltipSurface } from '../../skills';
import { type SkillHoverState } from './useSkillHover';

const SKILL_COMMAND_HOVER_TOOLTIP_ID = 'skill-command-hover-tooltip';

export type SkillHoverTooltipProps = {
  editor: Editor | null;
  hover: SkillHoverState;
};

export const SkillHoverTooltip = memo(({ editor, hover }: SkillHoverTooltipProps) => {
  const virtualRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const commandEl = hover.commandEl;
    const previousDescribedBy = commandEl.getAttribute('aria-describedby');

    commandEl.setAttribute('aria-describedby', SKILL_COMMAND_HOVER_TOOLTIP_ID);

    return () => {
      if (previousDescribedBy === null) {
        commandEl.removeAttribute('aria-describedby');
      } else {
        commandEl.setAttribute('aria-describedby', previousDescribedBy);
      }
    };
  }, [hover.commandEl]);

  useEffect(() => {
    const el = virtualRef.current;

    if (!el) {
      return;
    }

    const syncAnchor = () => {
      const rect = hover.getRect();

      el.style.left = `${rect.left}px`;
      el.style.top = `${rect.top}px`;
      el.style.width = `${rect.width}px`;
      el.style.height = `${rect.height}px`;
    };

    syncAnchor();

    window.addEventListener('scroll', syncAnchor, true);
    window.addEventListener('resize', syncAnchor);

    const editorDom = editor?.view.dom;

    editorDom?.addEventListener('scroll', syncAnchor);

    return () => {
      window.removeEventListener('scroll', syncAnchor, true);
      window.removeEventListener('resize', syncAnchor);
      editorDom?.removeEventListener('scroll', syncAnchor);
    };
  }, [editor, hover]);

  return (
    <SkillTooltipSurface
      delayDuration={0}
      label={
        <div id={SKILL_COMMAND_HOVER_TOOLTIP_ID}>
          <SkillExplanation skill={hover.skill} />
        </div>
      }
      open
      side="top"
    >
      <span className="pointer-events-none fixed" ref={virtualRef} tabIndex={-1} />
    </SkillTooltipSurface>
  );
});

SkillHoverTooltip.displayName = 'SkillHoverTooltip';
